import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * ServiceSelector - A visual dropdown with color indicators for services
 * Shows service color, name, price, and duration in a user-friendly format
 */
const ServiceSelector = ({ 
  services, 
  selectedServiceId, 
  onServiceChange,
  required = false,
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const selectedService = services?.find(s => s.id === selectedServiceId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSelect = (service) => {
    onServiceChange(service.id);
    setIsOpen(false);
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return `$${Number(price).toFixed(0)}`;
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  // Default color if service doesn't have one
  const getServiceColor = (service) => {
    return service?.color_code || '#6B7280';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Value Display / Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center px-3 py-2.5 
          border rounded-lg bg-white text-left
          transition-all duration-200
          ${disabled 
            ? 'bg-gray-100 cursor-not-allowed border-gray-200' 
            : isOpen 
              ? 'border-yellow-400 ring-2 ring-yellow-500 ring-opacity-50' 
              : 'border-gray-300 hover:border-gray-400 cursor-pointer'
          }
        `}
        data-testid="service-selector-trigger"
      >
        {selectedService ? (
          <>
            <div 
              className="w-4 h-4 rounded flex-shrink-0 mr-3"
              style={{ backgroundColor: getServiceColor(selectedService) }}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 truncate">
                {selectedService.service_name}
              </div>
              <div className="text-sm text-gray-500">
                {formatPrice(selectedService.service_price)} • {formatDuration(selectedService.service_duration)}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="w-4 h-4 rounded bg-gray-300 flex-shrink-0 mr-3" />
            <div className="flex-1 text-gray-500">Select a service</div>
          </>
        )}
        <ChevronDown 
          className={`w-5 h-5 text-gray-400 flex-shrink-0 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid="service-selector-dropdown"
        >
          {services?.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              No services available
            </div>
          ) : (
            services?.map((service) => {
              const isSelected = service.id === selectedServiceId;
              return (
                <div
                  key={service.id}
                  onClick={() => handleSelect(service)}
                  className={`
                    flex items-center px-3 py-2.5 cursor-pointer
                    transition-colors duration-150
                    ${isSelected 
                      ? 'bg-yellow-50' 
                      : 'hover:bg-gray-50'
                    }
                  `}
                  data-testid={`service-option-${service.id}`}
                >
                  <div 
                    className="w-4 h-4 rounded flex-shrink-0 mr-3"
                    style={{ backgroundColor: getServiceColor(service) }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium truncate ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                      {service.service_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatPrice(service.service_price)} • {formatDuration(service.service_duration)}
                    </div>
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-yellow-500 flex-shrink-0 ml-2" />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Hidden input for form validation */}
      {required && (
        <input
          type="hidden"
          value={selectedServiceId || ''}
          required
        />
      )}
    </div>
  );
};

export default ServiceSelector;
